/**
 * Ruler graduations: spacing for the current zoom and label formatting.
 * Pure functions, see RulerController for the drawing.
 */

/**
 * Graduation spacing (world units) for a minimum on-screen gap of
 * `worldDistance`: the first configured spacing large enough, else a "nice"
 * value.
 */
export function graduationSpacing(
  worldDistance: number,
  spacings: readonly number[],
): number {
  // If worldDistance is 0 or very small, return a sensible default
  if (!worldDistance || worldDistance <= 0) {
    return spacings[4]; // Default to 0.2
  }

  for (const spacing of spacings) {
    if (spacing >= worldDistance) {
      return spacing;
    }
  }
  return niceSpacing(worldDistance);
}

/**
 * Compute a "nice" spacing value (1, 2, 5, 10, 20, 50, etc.) >= worldDistance.
 * This ensures readable graduations at any zoom level.
 */
export function niceSpacing(worldDistance: number): number {
  // Find the order of magnitude (e.g., 1234567 -> 1000000)
  const magnitude = Math.pow(10, Math.floor(Math.log10(worldDistance)));
  const normalized = worldDistance / magnitude;

  // Round up to a nice multiplier (1, 2, 5, or 10)
  let niceMultiplier: number;
  if (normalized <= 1) niceMultiplier = 1;
  else if (normalized <= 2) niceMultiplier = 2;
  else if (normalized <= 5) niceMultiplier = 5;
  else niceMultiplier = 10;

  return niceMultiplier * magnitude;
}

/**
 * Find the closest step position >= target, aligned to origin.
 */
export function closestHigherStep(
  origin: number,
  target: number,
  gap: number,
): number {
  const distance = target - origin;
  const numberOfSteps = Math.ceil(distance / gap);
  return numberOfSteps * gap + origin;
}

/**
 * Format a label value with intelligent number formatting.
 * Uses k/M suffixes for large numbers to keep labels compact.
 */
export function formatRulerLabel(value: number, spacing: number): string {
  const absValue = Math.abs(value);

  // For large numbers, use k/M suffixes
  if (absValue >= 1000000) {
    const mValue = value / 1000000;
    // Avoid decimals if it's a round number
    return mValue % 1 === 0 ? `${mValue}M` : `${mValue.toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    const kValue = value / 1000;
    // Avoid decimals if it's a round number
    return kValue % 1 === 0 ? `${kValue}k` : `${kValue.toFixed(1)}k`;
  }

  // For small numbers, use decimal precision based on spacing
  if (spacing < 0.05) return value.toFixed(2);
  if (spacing < 0.5) return value.toFixed(1);
  return value.toFixed(0);
}
